/**
 * Экран согласий абонента (п. 6.5 ТЗ).
 *
 * Это отдельный шаг ДО подписания договора: согласие на обработку персональных
 * данных и согласие на ПЭП фиксируются как самостоятельные факты со своими
 * timestamp, а не «заодно» с подписью. Планшет на этом шаге передаётся абоненту.
 */

import { useEffect, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { Application, ConsentKind, ConsentText } from '../api/types'
import { Alert, Button, Loading, Steps } from '../components/ui'
import { fullName } from '../lib/format'

interface Props {
  application: Application
  onDone: (application: Application) => void
  onBack: () => void
}

const KINDS: { kind: ConsentKind; title: string }[] = [
  {
    kind: 'personal_data',
    title:
      'Я даю согласие на обработку моих персональных данных в соответствии с Федеральным ' +
      'законом № 152-ФЗ на условиях, изложенных выше.',
  },
  {
    kind: 'simple_signature',
    title:
      'Я согласен(на) подписать договор простой электронной подписью — росчерком на ' +
      'экране устройства — и признаю такой документ равнозначным бумажному.',
  },
]

export function ConsentScreen({ application, onDone, onBack }: Props) {
  const [text, setText] = useState<ConsentText | null>(null)
  const [loadError, setLoadError] = useState<ApiError | null>(null)
  const [checked, setChecked] = useState<Record<ConsentKind, boolean>>(() => {
    const given = new Set(application.consents.filter((c) => c.accepted).map((c) => c.kind))
    return {
      personal_data: given.has('personal_data'),
      simple_signature: given.has('simple_signature'),
    }
  })
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoadError(null)
    api
      .consentText()
      .then(setText)
      .catch((err) => setLoadError(err instanceof ApiError ? err : new ApiError(0, 'Ошибка')))
  }

  useEffect(load, [])

  const allChecked = KINDS.every(({ kind }) => checked[kind])

  const submit = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      // Каждое согласие — отдельный запрос: так у них независимые timestamp,
      // а частичный сбой не порождает записи «согласие без согласия».
      for (const { kind } of KINDS) {
        await api.addConsent(application.id, kind)
      }
      onDone(await api.getApplication(application.id))
    } catch (err) {
      setSaveError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Steps current={4} />
      <div className="card">
        <h2>Согласия абонента</h2>
        <p className="subtitle">
          Передайте планшет абоненту. {fullName(application) || 'Абонент'} должен прочитать
          текст и подтвердить согласия — без них договор подписать нельзя.
        </p>

        {loadError && (
          <Alert kind="error" title="Не удалось загрузить текст согласия" onRetry={load}>
            {loadError.message}
          </Alert>
        )}

        {!text && !loadError && <Loading text="Загружаем текст согласия…" />}

        {text && (
          <>
            {text.is_stub && (
              <Alert kind="warn" title="Текст-заглушка">
                Показан демонстрационный текст этапа разработки. Боевую редакцию
                предоставляет юрист заказчика (настройка CONSENT_TEXT_PATH).
              </Alert>
            )}

            <div className="consent-text">{text.text}</div>

            {KINDS.map(({ kind, title }) => (
              <label key={kind} className={`checkbox ${checked[kind] ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked[kind]}
                  onChange={(event) =>
                    setChecked((prev) => ({ ...prev, [kind]: event.target.checked }))
                  }
                />
                <span className="text">{title}</span>
              </label>
            ))}

            {saveError && (
              <Alert
                kind="error"
                title={saveError.retryable ? 'Согласие не зафиксировано' : 'Отказ сервера'}
                onRetry={saveError.retryable ? submit : undefined}
                onDismiss={() => setSaveError(null)}
              >
                {saveError.message}
              </Alert>
            )}

            {!allChecked && (
              <p className="subtitle" style={{ marginBottom: 12 }}>
                Если абонент не согласен — оформите подключение на бумажном договоре.
              </p>
            )}

            <div className="actions">
              <Button variant="secondary" onClick={onBack} disabled={saving}>
                Назад
              </Button>
              <Button onClick={submit} disabled={!allChecked} busy={saving}>
                Подтвердить и перейти к подписи
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  )
}
